import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Sparkles, Copy, Palette, ImageIcon } from "lucide-react";

const ESTILOS = ["vibrante", "minimalista", "premium", "juvenil", "clasico"];

export default function BrandingEditor({ plazaId }) {
  const [plaza, setPlaza] = useState(null);
  const [form, setForm] = useState({
    logo_url: "", color_primary: "#FF3366", color_accent: "#D4FF00",
    color_bg: "#F9F9F8", color_text: "#18181B", tagline: "",
    descripcion: "", hero_image_url: "",
  });
  const [prompts, setPrompts] = useState(null);
  const [gen, setGen] = useState({ estilo: "vibrante", concepto: "", paleta: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/plazas/${plazaId}`).then(r => {
    setPlaza(r.data);
    setForm(f => ({
      ...f,
      logo_url: r.data.logo_url || "",
      color_primary: r.data.color_primary || "#FF3366",
      color_accent: r.data.color_accent || "#D4FF00",
      color_bg: r.data.color_bg || "#F9F9F8",
      color_text: r.data.color_text || "#18181B",
      tagline: r.data.tagline || "",
      descripcion: r.data.descripcion || "",
      hero_image_url: r.data.hero_image_url || "",
    }));
  });

  useEffect(() => { if (plazaId) load(); /* eslint-disable-next-line */ }, [plazaId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Máximo 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.patch(`/plazas/${plazaId}/branding`, form);
      toast.success("Branding actualizado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const generarPrompts = async () => {
    try {
      const { data } = await api.post(`/plazas/${plazaId}/generate-ai-prompts`, gen);
      setPrompts(data);
      toast.success("Prompts generados. Cópialos y pégalos en tu IA favorita.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copiado"); } catch { toast.error("No se pudo copiar"); }
  };

  if (!plaza) return <div className="p-8 text-zinc-500">Cargando…</div>;

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-5">
        <div>
          <div className="overline text-zinc-500 mb-1"><Palette className="w-3 h-3 inline mr-1 -mt-0.5" /> Identidad visual</div>
          <div className="font-display font-black text-2xl tracking-tighter">Branding de {plaza.nombre}</div>
          <p className="text-sm text-zinc-600 mt-1">Personaliza logo, colores y prompts IA para tu plaza (white-label).</p>
        </div>

        <div>
          <Label>Logo (máx 500KB)</Label>
          <div className="mt-1 flex items-center gap-3">
            <div className="w-24 h-24 border border-zinc-200 rounded-sm grid place-items-center bg-zinc-50 overflow-hidden">
              {form.logo_url ? <img src={form.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
                : <ImageIcon className="w-8 h-8 text-zinc-300" />}
            </div>
            <label className="cursor-pointer">
              <input data-testid="brand-logo-upload" type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
              <span className="inline-flex items-center gap-1.5 px-4 h-10 rounded-sm border border-zinc-300 hover:border-zinc-900 text-sm font-medium">
                <Upload className="w-4 h-4" /> Subir logo
              </span>
            </label>
            {form.logo_url && <button onClick={() => set("logo_url", "")} className="text-xs text-red-600 hover:underline">Quitar</button>}
          </div>
        </div>

        <div>
          <Label>Imagen Hero (URL, opcional)</Label>
          <Input data-testid="brand-hero" value={form.hero_image_url} onChange={e => set("hero_image_url", e.target.value)} placeholder="https://..." className="mt-1 rounded-sm h-11" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Color primario</Label>
            <div className="mt-1 flex items-center gap-2">
              <input data-testid="brand-color-primary" type="color" value={form.color_primary} onChange={e => set("color_primary", e.target.value)} className="w-11 h-11 rounded-sm border border-zinc-300 cursor-pointer" />
              <Input value={form.color_primary} onChange={e => set("color_primary", e.target.value)} className="rounded-sm h-11 font-mono" />
            </div>
          </div>
          <div>
            <Label>Color acento</Label>
            <div className="mt-1 flex items-center gap-2">
              <input data-testid="brand-color-accent" type="color" value={form.color_accent} onChange={e => set("color_accent", e.target.value)} className="w-11 h-11 rounded-sm border border-zinc-300 cursor-pointer" />
              <Input value={form.color_accent} onChange={e => set("color_accent", e.target.value)} className="rounded-sm h-11 font-mono" />
            </div>
          </div>
          <div>
            <Label>Fondo</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={form.color_bg} onChange={e => set("color_bg", e.target.value)} className="w-11 h-11 rounded-sm border border-zinc-300 cursor-pointer" />
              <Input value={form.color_bg} onChange={e => set("color_bg", e.target.value)} className="rounded-sm h-11 font-mono" />
            </div>
          </div>
          <div>
            <Label>Texto</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={form.color_text} onChange={e => set("color_text", e.target.value)} className="w-11 h-11 rounded-sm border border-zinc-300 cursor-pointer" />
              <Input value={form.color_text} onChange={e => set("color_text", e.target.value)} className="rounded-sm h-11 font-mono" />
            </div>
          </div>
        </div>

        <div>
          <Label>Tagline (frase corta)</Label>
          <Input data-testid="brand-tagline" value={form.tagline} onChange={e => set("tagline", e.target.value)} placeholder="Ej: El corazón comercial del centro" className="mt-1 rounded-sm h-11" />
        </div>
        <div>
          <Label>Descripción larga</Label>
          <Textarea data-testid="brand-desc" value={form.descripcion} onChange={e => set("descripcion", e.target.value)} rows={3} className="mt-1 rounded-sm" />
        </div>

        <Button data-testid="brand-save" onClick={save} disabled={loading} className="rounded-sm bg-[#FF3366] hover:bg-[#E62958] h-11">
          {loading ? "Guardando…" : "Guardar branding"}
        </Button>
      </div>

      {/* Preview + AI prompts */}
      <div className="space-y-4">
        <div className="rounded-sm border border-zinc-200 overflow-hidden">
          <div className="p-4" style={{ background: `linear-gradient(135deg, ${form.color_primary}22 0%, ${form.color_accent}33 100%)`, color: form.color_text }}>
            {form.logo_url ? (
              <img src={form.logo_url} alt="preview" className="h-16 object-contain mx-auto" />
            ) : (
              <div className="font-display font-black text-2xl tracking-tighter text-center py-4">{plaza.nombre}</div>
            )}
          </div>
          <div className="p-4 bg-white space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Preview</div>
            <div className="text-sm italic text-zinc-700">"{form.tagline || plaza.nombre}"</div>
            <div className="flex gap-1 mt-2">
              <button style={{ background: form.color_primary, color: "#fff" }} className="text-xs px-3 py-1.5 rounded-sm font-medium">Primario</button>
              <button style={{ background: form.color_accent, color: form.color_text }} className="text-xs px-3 py-1.5 rounded-sm font-medium">Acento</button>
            </div>
          </div>
        </div>

        <div className="rounded-sm border border-zinc-200 p-5 bg-white">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FF3366]" />
            <div className="font-display font-bold tracking-tight">Prompts IA para tu logo</div>
          </div>
          <p className="text-xs text-zinc-500 mt-1">Genera prompts optimizados para MidJourney, DALL·E, Nano Banana.</p>

          <div className="mt-3 space-y-2">
            <div>
              <Label className="text-xs">Estilo</Label>
              <Select value={gen.estilo} onValueChange={v => setGen(g => ({ ...g, estilo: v }))}>
                <SelectTrigger data-testid="brand-ai-estilo" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ESTILOS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input value={gen.concepto} onChange={e => setGen(g => ({ ...g, concepto: e.target.value }))} placeholder="Concepto (opcional)" className="rounded-sm h-9 text-sm" />
            <Input value={gen.paleta} onChange={e => setGen(g => ({ ...g, paleta: e.target.value }))} placeholder="Paleta (opcional)" className="rounded-sm h-9 text-sm" />
          </div>
          <Button data-testid="brand-ai-generate" onClick={generarPrompts} className="mt-3 w-full h-9 rounded-sm bg-zinc-900 hover:bg-[#FF3366]">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Generar prompts
          </Button>

          {prompts && (
            <div className="mt-4 space-y-3">
              {["logo", "hero", "pattern"].map(k => (
                <div key={k} className="border border-zinc-200 rounded-sm p-3 bg-zinc-50">
                  <div className="flex items-center justify-between">
                    <span className="overline text-zinc-500">{k}</span>
                    <button data-testid={`copy-${k}`} onClick={() => copy(prompts[k])} className="text-zinc-500 hover:text-[#FF3366]">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-xs mt-1 text-zinc-700 leading-relaxed">{prompts[k]}</p>
                </div>
              ))}
              <div className="text-[11px] text-zinc-500 italic">{prompts.note}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
